import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useWalletContext } from '@/contexts/WalletContext'
import useIsMobile from '@/hooks/useIsMobile'
import useIsCondensed from '@/hooks/useIsCondensed'
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation'
import { useUser } from '@/hooks/useUser'
import { clearGuestSession } from '@/lib/guestStorage'
import { getUSDCBalance } from '@/lib/crypto/wallet'
import { TilingLayoutManager } from '@/lib/TilingLayoutManager'
import { theme } from '@/lib/theme'
import { useStorage } from '@/contexts/StorageContext'
import { getSelectedIdentity } from '@/types/UserSettings'

const ASSISTANT_NAME = import.meta.env.VITE_ASSISTANT_NAME!

import AuthWindow from './AuthWindow'
import ArtifactWindowManager from './artifacts/ArtifactWindowManager'
import ClockWidget from './ClockWidget'
import CookieConsent from './CookieConsent'
import UsageLimitBanner from './UsageLimitBanner'
import ChatApplication from './chat/Chat'
import FileExplorer from './FileExplorer'
import MobileDesktop from './MobileDesktop'
import Profile from './Profile'
import Representation from './Representation'
import Settings from './Settings'
import StippledBackground from './StippledBackground'
import Subscription from './Subscription'
import TopNav from './ui/TopNav'
import Window from './ui/Window'
import WeatherWidget from './WeatherWidget'
import WorkingRepresentationAnimated from './WorkingRepresentationAnimated'
import { MarketplacePurchaseExecutor } from './MarketplacePurchaseExecutor'
import DesktopTutorial from './tutorial/DesktopTutorial'

// Define a context for desktop functions
interface DesktopContext {
  openWindow: (
    title: string,
    contentType: string,
    size?: { width: number | string; height: number | string },
    initialPosition?: { x: number; y: number },
    extraData?: object
  ) => number | undefined // Return the ID of the newly created window or undefined
  closeWindow: (id: number) => void
  closeAllWindows: () => void
  focusWindow: (id: number) => void
  currentWindowId?: number
  gridSize: number
  activeArtifactWindowId: number | null
  setActiveArtifactWindowId: (id: number | null) => void
}

export const DesktopContext = createContext<DesktopContext | null>(null)

// Define a type for our window objects
interface WindowInstance {
  id: number
  title: string
  zIndex: number
  contentType?: 'auth' | 'fileExplorer' | 'settings' | 'chat' | 'profile' | 'representation'
  initialSize?: { width: number | string; height: number | string }
  initialPosition?: { x: number; y: number }
  extraData?: object
  emoji?: string
  isMinimized?: boolean
  isMaximized?: boolean
}

const GRID_SIZE = 50
const DEBOUNCE_DELAY = 300 // 300ms debounce delay

const desktopStyles = {
  container: {
    width: '100vw',
    height: '100vh',
    fontFamily: theme.fontFamily.mono,
    position: 'relative' as const,
    overflow: 'hidden' as const,
    backgroundSize: '5px 5px',
    paddingTop: '32px',
  },
  widgetsContainer: {
    position: 'absolute' as const,
    top: '60px',
    right: theme.spacing.xl,
    zIndex: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing.md,
  },
  windowsContainer: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const,
    zIndex: 1,
    transform: 'scale(1)',
    transformOrigin: 'center center',
    transition: 'transform 0.3s ease-out',
  },
}

export default function Desktop() {
  const [windows, setWindows] = useState<WindowInstance[]>([])
  const [activeWindowId, setActiveWindowId] = useState<number | null>(null)
  const [nextWindowId, setNextWindowId] = useState<number>(1)
  const [_highestZIndex, setHighestZIndex] = useState<number>(0)
  const [activeArtifactWindowId, setActiveArtifactWindowId] = useState<
    number | null
  >(null)
  const lastOpenWindowCall = useRef<number>(0)
  const hasAutoOpenedChat = useRef<boolean>(false)

  // NOTE: useIsMobile detects touch-centric devices (coarse pointer / mobile UA),
  // NOT viewport width. For width-based responsive behavior, use useIsCondensed below.
  const isMobile = useIsMobile()

  // Use a wider condensed breakpoint when multiple windows are open,
  // but allow widgets to stay visible longer when there's only a single
  // non-minimized window (maximized or others minimized).
  const nonMinimizedWindows = windows.filter((w) => !w.isMinimized).length
  const condensedBreakpoint = nonMinimizedWindows > 1 ? 1175 : 975
  const isCondensed = useIsCondensed(condensedBreakpoint) // Width-based (not device-based)
  const isNarrow = useIsCondensed(850) // Narrow width (matching social icons) forces single-window mode
  const [fontMode, setFontMode] = useState<
    'departure-mono' | 'open-dyslexic' | 'system'
  >(() => {
    // Initialize from localStorage or default to 'departure-mono'
    if (typeof window !== 'undefined') {
      const savedFont = localStorage.getItem('kyogre-font-mode')
      if (savedFont === 'departure-mono' || savedFont === 'open-dyslexic' || savedFont === 'system') {
        return savedFont
      }
    }
    return 'departure-mono'
  })
  const { toggleTheme, colors } = useThemeMode()
  const walletContext = useWalletContext()
  const { t, i18n } = useTranslation()

  const [draggedWindowId, setDraggedWindowId] = useState<number | null>(null)
  const [dragOverWindowId, setDragOverWindowId] = useState<number | null>(null)

  // Save font mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('kyogre-font-mode', fontMode)
  }, [fontMode])

  // Store window states before maximizing so we can restore them
  const [savedWindowStates, setSavedWindowStates] = useState<
    Map<number, { isMinimized: boolean }>
  >(new Map())

  // Track web search state for the chat window
  const [chatWebSearchEnabled, setChatWebSearchEnabled] = useState(false)
  const [chatWebSearchToggle, setChatWebSearchToggle] = useState<
    (() => void) | null
  >(null)

  // Track text size state for the chat window
  const [chatTextSize, setChatTextSize] = useState(14)
  const [chatTextSizeHandler, setChatTextSizeHandler] = useState<
    ((size: number) => void) | null
  >(null)
  const [showTextSizeDropdown, setShowTextSizeDropdown] = useState(false)
  const textSizeDropdownRef = useRef<HTMLDivElement>(null)

  // Track wallet state for the chat window
  const [hasWallet, setHasWallet] = useState(false)
  const [walletAddressCopied, setWalletAddressCopied] = useState(false)
  const [walletBalance, setWalletBalance] = useState<string>('0.00')

  // Track guest mode state for the chat window
  const [isGuestMode, setIsGuestMode] = useState(false)

  // Manage login modal state independently
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Manage subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [subscriptionSuccessMessage, setSubscriptionSuccessMessage] = useState<string | null>(null)

  // Track usage limit banner state
  const [showUsageLimitBanner, setShowUsageLimitBanner] = useState(false)
  const [usageLimitResetTime, setUsageLimitResetTime] = useState<string>('')

  // Track selected identity ID to detect changes
  const [selectedIdentity, setSelectedIdentity] = useState<string>(
    () => getSelectedIdentity()
  )

  // Tiling layout (always enabled)
  const useTiledLayout = true // Always use tiled layout
  const layoutManagerRef = useRef(new TilingLayoutManager())
  const [windowBounds, setWindowBounds] = useState<
    Map<number, { x: number; y: number; width: number; height: number }>
  >(new Map())
  const widgetsContainerRef = useRef<HTMLDivElement>(null)
  const { user, isLoading: isLoadingAuth } = useUser()
  const { identities } = useStorage()

  const focusWindow = useCallback(
    (id: number) => {
      // Check if window is minimized
      const targetWindow = windows.find((w) => w.id === id)
      const isMinimized = targetWindow?.isMinimized

      // If already active and not minimized, don't do anything (prevents blurring inputs)
      if (activeWindowId === id && !isMinimized) return

      const activeElement = document.activeElement
      // Only blur if the active element is NOT in the window we're focusing
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          (activeElement as HTMLElement).contentEditable === 'true')
      ) {
        const targetWindowElement = document.querySelector(
          `[data-window-id="${id}"]`
        )
        const isActiveElementInTargetWindow =
          targetWindowElement?.contains(activeElement)

        // Don't blur inputs that are in the window we're focusing
        if (!isActiveElementInTargetWindow) {
          ;(activeElement as HTMLElement).blur()
        }
      }

      let newZIndex = 0
      setHighestZIndex((prev) => {
        newZIndex = prev + 1
        return newZIndex
      })

      setWindows((prevWindows) => {
        const anyWindowMaximized = prevWindows.some((w) => w.isMaximized)

        // If any window is maximized, un-maximize and restore all windows to saved states
        if (anyWindowMaximized) {
          return prevWindows.map((w) => {
            const savedState = savedWindowStates.get(w.id)
            return {
              ...w,
              isMaximized: false,
              isMinimized: savedState?.isMinimized || false,
              zIndex: w.id === id ? newZIndex : w.zIndex,
            }
          })
        }

        // Normal focus - just expand the target window and update z-index
        return prevWindows.map((w) =>
          w.id === id ? { ...w, zIndex: newZIndex, isMinimized: false } : w
        )
      })

      setActiveWindowId(id)
      // Clear artifact window focus when regular window is focused
      setActiveArtifactWindowId(null)
    },
    [activeWindowId, windows, savedWindowStates]
  )

  const openWindow = useCallback(
    (
      appTitle: string,
      contentType: WindowInstance['contentType'],
      emoji: string,
      initialSize?: WindowInstance['initialSize'],
      initialPosition?: { x: number; y: number },
      extraData?: object
    ) => {
      // Debounce mechanism to prevent rapid successive calls
      const now = Date.now()
      if (now - lastOpenWindowCall.current < DEBOUNCE_DELAY) {
        return undefined // Return early if called too soon after last call
      }
      lastOpenWindowCall.current = now

      // Check if a window of this type already exists
      const existingWindow = windows.find((w) => w.contentType === contentType)
      if (existingWindow) {
        // Focus the existing window instead of creating a new one
        focusWindow(existingWindow.id)
        return existingWindow.id
      }

      let newZIndex = 0
      setHighestZIndex((prev) => {
        newZIndex = prev + 1
        return newZIndex
      })

      const newWindowIdToAssign = nextWindowId

      // Check if any window is currently maximized
      const anyWindowMaximized = windows.some((w) => w.isMaximized)

      const newWindow: WindowInstance = {
        id: newWindowIdToAssign,
        title: `${appTitle}`,
        zIndex: newZIndex,
        contentType,
        initialSize: isMobile ? { width: '100%', height: '100%' } : initialSize,
        initialPosition: isMobile ? { x: 0, y: 0 } : initialPosition,
        extraData,
        emoji,
        isMinimized: false,
        isMaximized: false,
      }

      // If a window is maximized, un-maximize it and restore all windows when opening new window
      if (anyWindowMaximized) {
        setWindows((prevWindows) => {
          const restoredWindows = prevWindows.map((w) => {
            const savedState = savedWindowStates.get(w.id)
            return {
              ...w,
              isMaximized: false,
              isMinimized: savedState?.isMinimized || false,
            }
          })
          return [...restoredWindows, newWindow]
        })
      } else {
        setWindows((prevWindows) => [...prevWindows, newWindow])
      }

      setActiveWindowId(newWindow.id)
      setNextWindowId((prevId) => prevId + 1)

      return newWindow.id
    },
    [nextWindowId, windows, focusWindow, savedWindowStates]
  )

  useEffect(() => {
    if (!isLoadingAuth && !user) {
      // Close all windows when user signs out
      setWindows([])
    }
  }, [user, isLoadingAuth])

  const closeWindow = useCallback(
    (id: number) => {
      setWindows((prevWindows) => {
        const remainingWindows = prevWindows.filter((w) => w.id !== id)
        if (activeWindowId === id) {
          if (remainingWindows.length > 0) {
            const nextActiveWindow = remainingWindows.reduce((prev, current) =>
              prev.zIndex > current.zIndex ? prev : current
            )
            setActiveWindowId(nextActiveWindow.id)
          } else {
            setActiveWindowId(null)
          }
        }
        return remainingWindows
      })
    },
    [activeWindowId]
  )

  const closeAllWindows = useCallback(() => {
    setWindows([])
    setActiveWindowId(null)
  }, [])

  const toggleMinimize = useCallback(
    (id: number) => {
      setWindows((prevWindows) => {
        const targetWindow = prevWindows.find((w) => w.id === id)
        const anyWindowMaximized = prevWindows.some((w) => w.isMaximized)

        // If the target window is currently maximized, un-maximize and then minimize it
        if (targetWindow?.isMaximized) {
          // Restore all windows to their saved states (un-maximize), then minimize the target
          const newWindows = prevWindows.map((w) => {
            const savedState = savedWindowStates.get(w.id)
            return {
              ...w,
              isMaximized: false,
              isMinimized:
                w.id === id ? true : savedState?.isMinimized || false,
            }
          })

          return newWindows
        }

        // If a different window is maximized and we're trying to restore a minimized window,
        // just expand the clicked window and un-maximize (don't restore other windows from saved state)
        if (anyWindowMaximized && targetWindow?.isMinimized) {
          const newWindows = prevWindows.map((w) => ({
            ...w,
            isMaximized: false,
            // Only expand the clicked window, keep others in their current state
            isMinimized: w.id === id ? false : w.isMinimized,
          }))

          return newWindows
        }

        // If any window is maximized and we're trying to minimize a non-minimized window,
        // just toggle it to minimized (don't un-maximize the other window)
        if (anyWindowMaximized && !targetWindow?.isMinimized) {
          return prevWindows.map((w) =>
            w.id === id ? { ...w, isMinimized: true } : w
          )
        }

        // Normal minimize toggle
        return prevWindows.map((w) =>
          w.id === id ? { ...w, isMinimized: !w.isMinimized } : w
        )
      })
    },
    [savedWindowStates]
  )

  const toggleMaximize = useCallback(
    (id: number) => {
      setWindows((prevWindows) => {
        const targetWindow = prevWindows.find((w) => w.id === id)
        if (!targetWindow) {
          return prevWindows
        }

        // Can't maximize if there's only one non-minimized window AND the target is already expanded
        const nonMinimizedCount = prevWindows.filter(
          (w) => !w.isMinimized
        ).length
        const isTargetMinimized = targetWindow.isMinimized
        const isCurrentlyMaximized = targetWindow.isMaximized

        // Block maximize only if:
        // 1. There's only 1 window total, OR
        // 2. There's only 1 expanded window AND the target is already that expanded window
        // BUT: Always allow un-maximizing (if window is currently maximized)
        if (
          !isCurrentlyMaximized &&
          (prevWindows.length === 1 ||
            (nonMinimizedCount === 1 && !isTargetMinimized))
        ) {
          return prevWindows
        }
        const anyWindowMaximized = prevWindows.some((w) => w.isMaximized)

        if (!isCurrentlyMaximized) {
          // MAXIMIZING: Save current states ONLY if no window is currently maximized
          // Only save states if this is the FIRST maximize (no window currently maximized)
          if (!anyWindowMaximized) {
            const savedStates = new Map<number, { isMinimized: boolean }>()
            prevWindows.forEach((w) => {
              savedStates.set(w.id, { isMinimized: w.isMinimized || false })
            })

            setSavedWindowStates(savedStates)
          } else {
            // Another window is already maximized - update saved states for any NEW windows
            const updatedSavedStates = new Map(savedWindowStates)
            let hasNewWindows = false

            prevWindows.forEach((w) => {
              if (!updatedSavedStates.has(w.id)) {
                // This is a new window that was opened while maximized - save its current state
                updatedSavedStates.set(w.id, {
                  isMinimized: w.isMinimized || false,
                })
                hasNewWindows = true
              }
            })

            if (hasNewWindows) {
              setSavedWindowStates(updatedSavedStates)
            }
          }

          const newWindows = prevWindows.map((w) => {
            if (w.id === id) {
              // Maximize this window - must NOT be minimized!
              return { ...w, isMaximized: true, isMinimized: false }
            } else {
              // Minimize all other windows
              return { ...w, isMinimized: true, isMaximized: false }
            }
          })

          // Set the maximized window as active
          setActiveWindowId(id)

          return newWindows
        } else {
          // UN-MAXIMIZING: Restore all windows to their saved states
          const newWindows = prevWindows.map((w) => {
            const savedState = savedWindowStates.get(w.id)
            return {
              ...w,
              isMaximized: false,
              isMinimized: savedState?.isMinimized || false,
            }
          })

          return newWindows
        }
      })
    },
    [savedWindowStates]
  )

  const cycleWindows = useCallback(
    (direction: 'prev' | 'next') => {
      if (windows.length <= 1) return

      const currentIndex = windows.findIndex((w) => w.id === activeWindowId)
      let nextIndex

      if (direction === 'next') {
        nextIndex =
          currentIndex === -1 || currentIndex === windows.length - 1
            ? 0
            : currentIndex + 1
      } else {
        nextIndex =
          currentIndex === -1 || currentIndex === 0
            ? windows.length - 1
            : currentIndex - 1
      }

      const nextWindow = windows[nextIndex]
      const nextWindowId = nextWindow.id

      // Check if any window is currently maximized
      const anyWindowMaximized = windows.some((w) => w.isMaximized)

      if (anyWindowMaximized) {
        // If in maximized mode, maximize the next window (which will minimize others)
        toggleMaximize(nextWindowId)
      } else {
        // Normal mode - if the next window is minimized, don't expand it, just make it active
        if (nextWindow.isMinimized) {
          setActiveWindowId(nextWindowId)
        } else {
          // If not minimized, use normal focus behavior
          focusWindow(nextWindowId)
        }
      }
    },
    [windows, activeWindowId, focusWindow, toggleMaximize]
  )

  const handleWindowReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setWindows((prevWindows) => {
        const reordered = [...prevWindows]
        const [movedWindow] = reordered.splice(fromIndex, 1)
        reordered.splice(toIndex, 0, movedWindow)

        // Save order to localStorage
        const windowOrder = reordered.map((w) => ({
          id: w.id,
          contentType: w.contentType,
        }))
        localStorage.setItem('windowOrder', JSON.stringify(windowOrder))

        return reordered
      })
    },
    []
  )

  const handleReorderActiveWindow = useCallback(
    (direction: 'left' | 'right') => {
      if (activeWindowId === null) return

      setWindows((prevWindows) => {
        const currentIndex = prevWindows.findIndex(
          (w) => w.id === activeWindowId
        )
        if (currentIndex === -1) return prevWindows

        const newIndex =
          direction === 'left'
            ? Math.max(0, currentIndex - 1)
            : Math.min(prevWindows.length - 1, currentIndex + 1)

        if (newIndex === currentIndex) return prevWindows

        const reordered = [...prevWindows]
        const [movedWindow] = reordered.splice(currentIndex, 1)
        reordered.splice(newIndex, 0, movedWindow)

        // Save order to localStorage
        const windowOrder = reordered.map((w) => ({
          id: w.id,
          contentType: w.contentType,
        }))
        localStorage.setItem('windowOrder', JSON.stringify(windowOrder))

        return reordered
      })
    },
    [activeWindowId]
  )

  const handleWindowDragStart = useCallback((windowId: number) => {
    setDraggedWindowId(windowId)
  }, [])

  const handleWindowDragOver = useCallback(
    (windowId: number) => {
      if (draggedWindowId === null || draggedWindowId === windowId) return
      setDragOverWindowId(windowId)
    },
    [draggedWindowId]
  )

  const handleWindowDrop = useCallback(
    (targetWindowId: number) => {
      if (draggedWindowId === null || draggedWindowId === targetWindowId) return

      const fromIndex = windows.findIndex((w) => w.id === draggedWindowId)
      const toIndex = windows.findIndex((w) => w.id === targetWindowId)

      if (fromIndex !== -1 && toIndex !== -1) {
        handleWindowReorder(fromIndex, toIndex)
      }

      setDraggedWindowId(null)
      setDragOverWindowId(null)
    },
    [draggedWindowId, windows, handleWindowReorder]
  )

  const handleWindowDragEnd = useCallback(() => {
    setDraggedWindowId(null)
    setDragOverWindowId(null)
  }, [])

  // Load and apply window order from localStorage
  useEffect(() => {
    const savedOrder = localStorage.getItem('windowOrder')
    if (!savedOrder || windows.length === 0) return

    try {
      const orderData: { id: number; contentType?: string }[] =
        JSON.parse(savedOrder)

      setWindows((prevWindows) => {
        // Create a map for quick lookup
        const windowMap = new Map(prevWindows.map((w) => [w.id, w]))

        // Reorder based on saved order, keeping any new windows at the end
        const reordered: WindowInstance[] = []
        const usedIds = new Set<number>()

        // Add windows in saved order
        for (const saved of orderData) {
          const window = windowMap.get(saved.id)
          if (window) {
            reordered.push(window)
            usedIds.add(saved.id)
          }
        }

        // Add any new windows that weren't in saved order
        for (const window of prevWindows) {
          if (!usedIds.has(window.id)) {
            reordered.push(window)
          }
        }

        return reordered
      })
    } catch (error) {
      console.error('Error loading window order:', error)
    }
  }, [windows.length]) // Only run when number of windows changes

  // Configure layout to always reserve consistent space for widgets/login modal
  useEffect(() => {
    if (isMobile) return

    // Always disable max width constraint - let natural layout handle it
    // Desktop.tsx handles widget space reservation directly in calculateLayout
    layoutManagerRef.current.setMaxWindowWidth(undefined)
  }, [isMobile])

  // Recalculate window positions when windows change
  useEffect(() => {
    if (isMobile) return

    const calculateLayout = () => {
      const tiledWindows = windows.map((w) => ({
        id: w.id,
        isMinimized: w.isMinimized,
        isMaximized: w.isMaximized,
        isFloating: false,
      }))

      const topNavHeight = 40
      const sidePadding = 24 // Padding on left/right sides
      const topPadding = 24 // Padding at top
      const bottomPadding = 0 // No bottom padding - windows extend to bottom

      // In condensed mode, use the full width for windows and hide widgets.
      // Otherwise, reserve consistent space on the right for widgets/login modal.
      let widgetAreaWidth = 0
      if (!isCondensed) {
        widgetAreaWidth = 380 // Default reserved space (matches login modal + widgets width)

        if (widgetsContainerRef.current && user) {
          // User is logged in: measure actual widgets to ensure perfect alignment
          const widgetRect = widgetsContainerRef.current.getBoundingClientRect()
          const measuredWidth = window.innerWidth - widgetRect.left + sidePadding
          // Use measured width if available, otherwise fall back to default
          widgetAreaWidth = measuredWidth > 150 ? measuredWidth : widgetAreaWidth
        }
        // If not logged in, always use default 380px for consistency
      }

      // Calculate available space with padding
      const viewportWidth = window.innerWidth - sidePadding * 2 - widgetAreaWidth
      const viewportHeight = window.innerHeight - topNavHeight - topPadding - bottomPadding

      const positions = layoutManagerRef.current.calculateLayout(
        tiledWindows,
        viewportWidth,
        viewportHeight
      )

      // Offset positions by padding
      const adjustedPositions = new Map()
      positions.forEach((bounds, id) => {
        adjustedPositions.set(id, {
          ...bounds,
          x: bounds.x + sidePadding,
          y: bounds.y + topPadding,
        })
      })

      setWindowBounds(adjustedPositions)
    }

    calculateLayout()

    // Recalculate on window resize
    window.addEventListener('resize', calculateLayout)
    return () => window.removeEventListener('resize', calculateLayout)
  }, [windows, isMobile, user, isCondensed]) // Re-run when windows change, user logs in/out, or condensed state changes

  // Keyboard navigation
  const handleScrollWindow = useCallback(
    (direction: 'up' | 'down') => {
      if (activeWindowId === null) return

      const activeWindow = document.querySelector(
        `[data-window-id="${activeWindowId}"]`
      )
      if (activeWindow) {
        const contentArea = activeWindow.querySelector(
          '.window-content'
        ) as HTMLElement
        if (contentArea) {
          const scrollAmount = 50
          const currentScrollTop = contentArea.scrollTop

          if (direction === 'up') {
            contentArea.scrollTop = Math.max(0, currentScrollTop - scrollAmount)
          } else {
            const maxScroll =
              contentArea.scrollHeight - contentArea.clientHeight
            contentArea.scrollTop = Math.min(
              maxScroll,
              currentScrollTop + scrollAmount
            )
          }
        }
      }
    },
    [activeWindowId]
  )

  const handleFocusInput = useCallback(() => {
    if (activeWindowId === null) return

    const activeWindow = document.querySelector(
      `[data-window-id="${activeWindowId}"]`
    )
    if (activeWindow) {
      const inputs = activeWindow.querySelectorAll(
        'input[type="text"], input[type="search"], input:not([type]), textarea, [contenteditable="true"]'
      )

      if (inputs.length > 0) {
        const firstInput = inputs[0] as HTMLElement
        firstInput.focus()
      }
    }
  }, [activeWindowId])

  const handleCloseActiveWindow = useCallback(() => {
    if (activeWindowId !== null) {
      closeWindow(activeWindowId)
    }
  }, [activeWindowId, closeWindow])

  const handleMaximizeActiveWindow = useCallback(() => {
    if (activeWindowId !== null) {
      toggleMaximize(activeWindowId)
    }
  }, [activeWindowId, toggleMaximize])

  const handleMinimizeActiveWindow = useCallback(() => {
    if (activeWindowId !== null) {
      toggleMinimize(activeWindowId)
    }
  }, [activeWindowId, toggleMinimize])

  const handleOpenChat = useCallback(() => {
    const identityPart = i18n.language === 'ja' ? '' : ` - ${selectedIdentity === 'default' ? ASSISTANT_NAME : selectedIdentity}`
    const title = `${t('navigation.chat')}${identityPart}`
    openWindow(title, 'chat', '💬', { width: 1200, height: 800 })
  }, [openWindow, t, i18n.language, selectedIdentity])

  const handleOpenFiles = useCallback(() => {
    openWindow(t('navigation.fileExplorer'), 'fileExplorer', '📁', {
      width: 900,
      height: 600,
    })
  }, [openWindow, t])

  const handleOpenSettings = useCallback(() => {
    openWindow(t('navigation.settings'), 'settings', '⚙️', {
      width: 500,
      height: 500,
    })
  }, [openWindow, t])

  const handleOpenProfile = useCallback(() => {
    openWindow(t('navigation.profile'), 'profile', '👤', {
      width: 600,
      height: 700,
    })
  }, [openWindow, t])

  const handleOpenRepresentation = useCallback(() => {
    openWindow(t('navigation.representation'), 'representation', '🧠', {
      width: 800,
      height: 600,
    })
  }, [openWindow, t])

  useKeyboardNavigation({
    isMobile,
    windowCount: windows.length,
    activeWindowId,
    onCycleWindows: cycleWindows,
    onScrollWindow: handleScrollWindow,
    onCloseWindow: handleCloseActiveWindow,
    onFocusInput: handleFocusInput,
    onReorderWindow: handleReorderActiveWindow,
    onMaximizeWindow: handleMaximizeActiveWindow,
    onMinimizeWindow: handleMinimizeActiveWindow,
    onOpenChat: handleOpenChat,
    onOpenFiles: handleOpenFiles,
    onOpenSettings: handleOpenSettings,
    onOpenProfile: handleOpenProfile,
    onOpenRepresentation: handleOpenRepresentation,
  })

  // Auto-open Chat window on first load
  useEffect(() => {
    if (!isLoadingAuth && !hasAutoOpenedChat.current) {
      hasAutoOpenedChat.current = true
      const identityPart = i18n.language === 'ja' ? '' : ` - ${selectedIdentity === 'default' ? ASSISTANT_NAME : selectedIdentity}`
      const title = `${t('navigation.chat')}${identityPart}`
      openWindow(title, 'chat', '💬', {
        width: 1200,
        height: 800,
      })
    }
  }, [isLoadingAuth, openWindow, t, i18n.language, selectedIdentity])

  // Start tutorial after first successful login per user (and if not completed)
  useEffect(() => {
    if (isLoadingAuth) return
    if (!user) return
    try {
      const tutorialId = 'desktop-walkthrough'
      const completedKey = `tutorial-completed-${tutorialId}`
      const completed = localStorage.getItem(completedKey) === 'true'
      const shownKey = `tutorial-shown-${tutorialId}-user-${(user as any).id || 'unknown'}`
      const alreadyShown = localStorage.getItem(shownKey) === 'true'
      if (!completed && !alreadyShown) {
        // Give DesktopTutorial a tick to mount and register window.desktopTutorial
        setTimeout(() => {
          try {
            ;(window as any).desktopTutorial?.start?.()
            localStorage.setItem(shownKey, 'true')
          } catch {}
        }, 0)
      }
    } catch {}
  }, [user, isLoadingAuth])

  // Force single-window maximized mode when in narrow width
  const wasNarrowRef = useRef(isNarrow)
  useEffect(() => {
    if (isMobile) return // Mobile already has its own layout logic
    if (windows.length === 0) return

    const wasNarrow = wasNarrowRef.current
    const isNowNarrow = isNarrow

    // Only act on transitions
    if (wasNarrow === isNowNarrow) return

    wasNarrowRef.current = isNowNarrow

    if (isNowNarrow) {
      // Entering narrow mode - maximize active window (or first non-minimized window)
      const anyMaximized = windows.some(w => w.isMaximized)
      if (!anyMaximized) {
        // Find the active window or first non-minimized window
        const targetWindow = activeWindowId
          ? windows.find(w => w.id === activeWindowId)
          : windows.find(w => !w.isMinimized) || windows[0]

        if (targetWindow && windows.length > 1) {
          toggleMaximize(targetWindow.id)
        }
      }
    } else {
      // Exiting narrow mode - un-maximize all windows
      const anyMaximized = windows.some(w => w.isMaximized)
      if (anyMaximized) {
        // Find the maximized window and toggle it off
        const maximizedWindow = windows.find(w => w.isMaximized)
        if (maximizedWindow) {
          toggleMaximize(maximizedWindow.id)
        }
      }
    }
  }, [isNarrow, isMobile, windows, activeWindowId, toggleMaximize])

  // Listen for storage changes to detect identity selection changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'settings') {
        setSelectedIdentity(getSelectedIdentity())
      }
    }

    // Also listen for custom event from same window (storage event doesn't fire in same window)
    const handleIdentityChange = () => {
      setSelectedIdentity(getSelectedIdentity())
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('identity-changed', handleIdentityChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('identity-changed', handleIdentityChange)
    }
  }, [])

  // Update chat window title when selected identity changes
  useEffect(() => {
    const identityPart = i18n.language === 'ja' ? '' : ` - ${selectedIdentity === 'default' ? ASSISTANT_NAME : selectedIdentity}`
    const newTitle = `${t('navigation.chat')}${identityPart}`

    setWindows((prevWindows) =>
      prevWindows.map((w) =>
        w.contentType === 'chat'
          ? { ...w, title: newTitle }
          : w
      )
    )
  }, [selectedIdentity, t, i18n.language])

  // Check for Stripe return URLs and show subscription modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const checkoutStatus = params.get('checkout')
    const portalStatus = params.get('portal')
    const sessionId = params.get('session_id')

    if (checkoutStatus === 'success') {
      setSubscriptionSuccessMessage('✨ Full Access Granted! Your subscription is now active.')
      setShowSubscriptionModal(true)

      // Store Stripe session ID for grace period verification
      if (sessionId) {
        localStorage.setItem('stripe_session_id', sessionId)
        localStorage.setItem('stripe_session_timestamp', Date.now().toString())
      }

      window.history.replaceState({}, '', window.location.pathname)
    } else if (checkoutStatus === 'cancel') {
      setSubscriptionSuccessMessage(null)
      setShowSubscriptionModal(true)
      window.history.replaceState({}, '', window.location.pathname)
    } else if (portalStatus === 'return') {
      setSubscriptionSuccessMessage('Subscription updated successfully')
      setShowSubscriptionModal(true)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  // Handle web search state changes for chat window
  const handleWebSearchStateChange = useCallback(
    (enabled: boolean, toggle: () => void) => {
      setChatWebSearchEnabled(enabled)
      setChatWebSearchToggle(() => toggle)
    },
    []
  )

  // Handle text size state changes for chat window
  const handleTextSizeStateChange = useCallback(
    (size: number, handler: (size: number) => void) => {
      setChatTextSize(size)
      setChatTextSizeHandler(() => handler)
    },
    []
  )

  // Close text size dropdown when clicking outside, pressing Escape, or after 4s inactivity.
  // We also reset the inactivity timer whenever the current text size changes so that
  // a long slider drag doesn't cause the dropdown to close mid-interaction.
  useEffect(() => {
    if (!showTextSizeDropdown) return

    // Auto-close after a short period of inactivity
    const autoCloseTimer = setTimeout(() => {
      setShowTextSizeDropdown(false)
    }, 7000)

    const handleClickOutside = (event: MouseEvent) => {
      if (
        textSizeDropdownRef.current &&
        !textSizeDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTextSizeDropdown(false)
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowTextSizeDropdown(false)
      }
    }

    // Delay adding the click-outside listener to avoid catching the opening click
    const listenerTimer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 0)

    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      clearTimeout(autoCloseTimer)
      clearTimeout(listenerTimer)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [showTextSizeDropdown, chatTextSize])

  // Handle wallet state changes for chat window
  const handleWalletStateChange = useCallback((walletState: boolean) => {
    setHasWallet(walletState)
  }, [])

  // Handle wallet icon click to copy address
  const handleWalletIconClick = useCallback(async () => {
    if (!walletContext.publicAddress) return

    try {
      // Copy address to clipboard (no passkey required)
      await navigator.clipboard.writeText(walletContext.publicAddress)

      // Show flash animation
      setWalletAddressCopied(true)

      // Reset flash after 500ms
      setTimeout(() => {
        setWalletAddressCopied(false)
      }, 500)
    } catch (error) {
      console.error('Error copying wallet address:', error)
    }
  }, [walletContext.publicAddress])

  // Fetch wallet balance periodically
  useEffect(() => {
    let mounted = true
    let intervalId: NodeJS.Timeout | null = null

    const fetchBalance = async () => {
      if (!walletContext.publicAddress || !hasWallet) {
        setWalletBalance('0.00')
        return
      }

      try {
        const balance = await getUSDCBalance(walletContext.publicAddress)
        if (mounted) {
          setWalletBalance(balance)
        }
      } catch (error) {
        console.error('Error fetching USDC balance:', error)
        if (mounted) {
          setWalletBalance('0.00')
        }
      }
    }

    if (walletContext.publicAddress && hasWallet) {
      fetchBalance()
      // Refresh balance every 30 seconds
      intervalId = setInterval(fetchBalance, 30000)
    }

    return () => {
      mounted = false
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [walletContext.publicAddress, hasWallet])

  // Handle guest mode state changes for chat window
  const handleGuestModeStateChange = useCallback((guestMode: boolean) => {
    setIsGuestMode(guestMode)
  }, [])

  // Handle usage limit reached
  const handleUsageLimitReached = useCallback((resetTime: string) => {
    setUsageLimitResetTime(resetTime)
    setShowUsageLimitBanner(true)
  }, [])

  // Handle opening subscription modal
  const handleOpenSubscription = useCallback(() => {
    setShowSubscriptionModal(true)
  }, [])

  // Handle usage limit banner close
  const handleUsageLimitBannerClose = useCallback(() => {
    setShowUsageLimitBanner(false)
  }, [])

  // Handle login button click
  const handleLoginClick = useCallback(() => {
    setShowLoginModal(prev => !prev)
  }, [])

  // Handle successful login
  const handleLoginSuccess = useCallback(() => {
    setShowLoginModal(false)
    // Discard guest session - user is logging into existing account
    clearGuestSession()
    // Reload the page to reinitialize session context with authenticated user
    window.location.reload()
  }, [])

  // Handle closing login modal
  const handleCloseLoginModal = useCallback(() => {
    setShowLoginModal(false)
  }, [])

  // Auto-focus chat input when chat window becomes active
  useEffect(() => {
    if (activeWindowId === null || isMobile) return

    const activeWindow = windows.find((w) => w.id === activeWindowId)
    if (activeWindow?.contentType === 'chat') {
      const targetWindowElement = document.querySelector(
        `[data-window-id="${activeWindowId}"]`
      )
      const chatInput = targetWindowElement?.querySelector(
        'input[type="text"]'
      ) as HTMLInputElement
      if (chatInput && !chatInput.disabled) {
        chatInput.focus()
      }
    }
  }, [activeWindowId, windows, isMobile])

  const desktopContextValue: DesktopContext = useMemo(
    () => ({
      openWindow: (title, contentType, size, initialPosition, extraData) =>
        openWindow(
          title,
          contentType as WindowInstance['contentType'],
          '',
          size,
          initialPosition,
          extraData
        ),
      closeWindow,
      closeAllWindows,
      focusWindow,
      currentWindowId: activeWindowId || undefined,
      gridSize: GRID_SIZE,
      activeArtifactWindowId,
      setActiveArtifactWindowId,
    }),
    [
      openWindow,
      closeWindow,
      closeAllWindows,
      focusWindow,
      activeWindowId,
      activeArtifactWindowId,
    ]
  )

  useEffect(() => {
    window.desktopContext = desktopContextValue

    return () => {
      delete window.desktopContext
    }
  }, [desktopContextValue])

  // Calculate font family based on fontMode for uniform fonts across entire site
  const fontFamily =
    fontMode === 'departure-mono'
      ? 'var(--font-departure-mono), monospace'
      : fontMode === 'open-dyslexic'
        ? 'var(--font-open-dyslexic), sans-serif'
        : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

  // Apply font to document.body so portals inherit it
  useEffect(() => {
    document.body.style.fontFamily = fontFamily
    return () => {
      document.body.style.fontFamily = ''
    }
  }, [fontFamily])

  // Render mobile-optimized layout on mobile devices
  if (isMobile) {
    return <MobileDesktop />
  }

  return (
    <DesktopContext.Provider value={desktopContextValue}>
      <div
        className="desktop"
        style={{
          ...desktopStyles.container,
          backgroundColor: colors.primaryBg,
          backgroundImage: 'none',
          transition: 'background-color 0.3s ease',
          paddingTop: '40px',
          paddingBottom: 0,
          margin: 0,
          fontFamily,
        }}
      >
        <StippledBackground />

        {/* Background purchase executor (SSE listener) */}
        {user && <MarketplacePurchaseExecutor />}

        {/* Top navigation bar - now shown on both desktop and mobile */}
        <TopNav
          fontMode={fontMode}
          onFontChange={setFontMode}
          onThemeToggle={toggleTheme}
          windows={windows.map((window) => ({
            id: window.id,
            title: window.title,
            contentType: window.contentType,
            isActive: activeWindowId === window.id,
            isMinimized: window.isMinimized,
          }))}
          onWindowClick={focusWindow}
          onWindowClose={closeWindow}
          activeWindowId={activeWindowId}
          onOpenChat={handleOpenChat}
          onOpenFileExplorer={() =>
            openWindow(t('navigation.fileExplorer'), 'fileExplorer', '📁', {
              width: 900,
              height: 600,
            })
          }
          onOpenSettings={() =>
            openWindow(t('navigation.settings'), 'settings', '⚙️', {
              width: 500,
              height: 500,
            })
          }
          onOpenProfile={() =>
            openWindow(t('navigation.profile'), 'profile', '👤', {
              width: 600,
              height: 700,
            })
          }
          onOpenRepresentation={() =>
            openWindow(t('navigation.representation'), 'representation', '🧠', {
              width: 800,
              height: 600,
            })
          }
          // Show login UI whenever there is no authenticated user, or when chat explicitly indicates guest mode
          isGuestMode={!user || isGuestMode}
          onLoginClick={handleLoginClick}
          showLoginModal={showLoginModal}
          onLoginSuccess={handleLoginSuccess}
          onCloseLoginModal={handleCloseLoginModal}
        />

        {!isCondensed && (
          <div
            ref={widgetsContainerRef}
            style={
              isMobile
                ? {
                    ...desktopStyles.widgetsContainer,
                    position: 'fixed' as const,
                    top: 'calc(50px + env(safe-area-inset-top))',
                    right: '12px',
                    zIndex: 0,
                  }
                : desktopStyles.widgetsContainer
            }
          >
            {user && <WeatherWidget />}
            {user && <ClockWidget />}
            {user && (
              <WorkingRepresentationAnimated
                disabled={windows.some(
                  (w) =>
                    w.contentType === 'representation' && !w.isMinimized
                )}
              />
            )}
          </div>
        )}

        <div
          style={
            isMobile
              ? {
                  ...desktopStyles.windowsContainer,
                  top: 'calc(40px + env(safe-area-inset-top))',
                  height: 'calc(100% - 40px - env(safe-area-inset-top))',
                }
              : desktopStyles.windowsContainer
          }
        >
          {windows.map((win) => {
            const tiledBounds =
              useTiledLayout && !isMobile ? windowBounds.get(win.id) : undefined

            // Create title bar actions for chat window
            const titleBarActions =
              win.contentType === 'chat' ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {/* Wallet indicator - only show if user has a wallet */}
                  {hasWallet && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        handleWalletIconClick()
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        backgroundColor: walletAddressCopied
                          ? colors.primaryAccent
                          : colors.secondaryBg,
                        border: `1px solid ${
                          walletAddressCopied
                            ? colors.primaryAccent
                            : colors.primaryBorder
                        }`,
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: colors.primaryText,
                        cursor: 'pointer',
                        userSelect: 'none' as const,
                        lineHeight: 1,
                        transition: 'all 0.2s ease-in-out',
                      }}
                      title={walletAddressCopied ? "Address copied!" : "Click to copy wallet address"}
                    >
                      <span style={{ fontSize: '12px' }}>💳</span>
                      <span style={{ fontSize: '11px', fontWeight: 500 }}>
                        ${walletBalance}
                      </span>
                    </div>
                  )}

                  {/* Text size control */}
                  {chatTextSizeHandler && (
                    <div
                      ref={textSizeDropdownRef}
                      className="text-size-control window-control-button"
                      draggable={false}
                      onMouseDown={(e) => {
                        e.stopPropagation()
                      }}
                      onPointerDown={(e) => {
                        e.stopPropagation()
                      }}
                      onDragStart={(e) => {
                        // Prevent window header drag/reorder when interacting with the text size control
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 8px',
                        backgroundColor: colors.secondaryBg,
                        border: `1px solid ${colors.primaryBorder}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        userSelect: 'none' as const,
                        lineHeight: 1,
                        overflow: 'hidden',
                        width: showTextSizeDropdown ? '170px' : '30px',
                        transition: 'width 0.25s ease-out',
                        willChange: showTextSizeDropdown ? 'width' : 'auto',
                      }}
                    >
                      <div
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowTextSizeDropdown(!showTextSizeDropdown)
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          width: '14px',
                        }}
                        title="Text Size"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ display: 'block' }}
                        >
                          <path
                            d="M3 7V5H13V7H9V17H7V7H3Z"
                            fill="currentColor"
                          />
                          <path
                            d="M14 9V7H21V9H18.5V19H16.5V9H14Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>

                      {showTextSizeDropdown && (
                        <div
                          onMouseDown={(e) => {
                            e.stopPropagation()
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation()
                          }}
                          onDragStart={(e) => {
                            // Extra safety: ensure no drag is initiated from inside the slider container
                            e.preventDefault()
                            e.stopPropagation()
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            width: '120px',
                          }}
                        >
                          <input
                            type="range"
                            min="10"
                            max="20"
                            step="1"
                            value={chatTextSize}
                            onInput={(e) => {
                              const newSize = Number.parseInt((e.target as HTMLInputElement).value, 10)
                              if (chatTextSizeHandler) {
                                chatTextSizeHandler(newSize)
                              }
                            }}
                            onChange={(e) => {
                              const newSize = Number.parseInt(e.target.value, 10)
                              if (chatTextSizeHandler) {
                                chatTextSizeHandler(newSize)
                              }
                            }}
                            onMouseDown={(e) => {
                              // Ensure dragging the thumb doesn't start a window drag
                              e.stopPropagation()
                            }}
                            onPointerDown={(e) => {
                              // Same for pointer events (touch, pen)
                              e.stopPropagation()
                            }}
                            style={{
                              width: '100%',
                              height: '4px',
                              cursor: 'ew-resize',
                              accentColor: colors.primaryAccent,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Web search toggle */}
                  {chatWebSearchToggle && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        chatWebSearchToggle()
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        backgroundColor: chatWebSearchEnabled
                          ? colors.webIndicatorOnBg
                          : colors.webIndicatorOffBg,
                        border: `1px solid ${
                          chatWebSearchEnabled
                            ? colors.webIndicatorOnBorder
                            : colors.webIndicatorOffBorder
                        }`,
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: colors.primaryText,
                        transition: 'all 0.2s ease-in-out',
                        cursor: 'pointer',
                        userSelect: 'none' as const,
                        lineHeight: 1,
                      }}
                      title={
                        chatWebSearchEnabled
                          ? t('chat.header.webSearchEnabled')
                          : t('chat.header.webSearchDisabled')
                      }
                    >
                      <span style={{ fontSize: '12px' }}>🌐</span>
                      <span style={{ fontWeight: 500 }}>
                        {chatWebSearchEnabled
                          ? t('chat.header.webSearchOn')
                          : t('chat.header.webSearchOff')}
                      </span>
                    </div>
                  )}
                </div>
              ) : undefined

            // Calculate if maximize is allowed for this specific window
            // Allow maximize if there are multiple windows total (>1)
            // This allows maximizing even when all other windows are minimized
            const canMaximize = windows.length > 1

            return (
              <Window
                key={win.id}
                id={win.id}
                title={win.title}
                isActive={activeWindowId === win.id}
                onFocus={() => {
                  focusWindow(win.id)
                }}
                onClose={() => closeWindow(win.id)}
                zIndex={win.zIndex}
                initialSize={win.initialSize}
                initialPosition={win.initialPosition || { x: 0, y: 0 }}
                draggable={false} // Always use tiled layout (no dragging)
                isMobile={isMobile}
                isMinimized={win.isMinimized}
                isMaximized={win.isMaximized}
                onMinimize={() => toggleMinimize(win.id)}
                onMaximize={() => toggleMaximize(win.id)}
                useTiledMode={!isMobile}
                windowCount={windows.length}
                canMaximize={canMaximize}
                tiledBounds={tiledBounds}
                titleBarActions={titleBarActions}
                onReorderDragStart={handleWindowDragStart}
                onReorderDragOver={handleWindowDragOver}
                onReorderDrop={handleWindowDrop}
                onReorderDragEnd={handleWindowDragEnd}
                isDraggedForReorder={draggedWindowId === win.id}
                isDragOverForReorder={dragOverWindowId === win.id}
                contentType={win.contentType}
              >
                {win.contentType === 'chat' && (
                  <ChatApplication
                    fontMode={fontMode}
                    onWebSearchStateChange={handleWebSearchStateChange}
                    onTextSizeStateChange={handleTextSizeStateChange}
                    onWalletStateChange={handleWalletStateChange}
                    onGuestModeStateChange={handleGuestModeStateChange}
                    onUsageLimitReached={handleUsageLimitReached}
                    onOpenSubscription={handleOpenSubscription}
                  />
                )}
                {win.contentType === 'fileExplorer' && (
                  <FileExplorer
                    extraData={win.extraData}
                    onActivateWindow={() => focusWindow(win.id)}
                  />
                )}
                {win.contentType === 'settings' && <Settings />}
                {win.contentType === 'profile' && <Profile />}
                {win.contentType === 'representation' && <Representation />}
                {win.contentType === 'auth' && (
                  <AuthWindow
                    onAuthSuccess={() => {}}
                    extraData={win.extraData}
                  />
                )}
              </Window>
            )
          })}

          {/* Artifact windows - still supported for file explorer */}
          <ArtifactWindowManager />
        </div>

        {/* Subscription Modal */}
        {showSubscriptionModal && (
          <Subscription
            onClose={() => {
              setShowSubscriptionModal(false)
              setSubscriptionSuccessMessage(null)
            }}
            initialSuccessMessage={subscriptionSuccessMessage}
          />
        )}

        {/* Cookie consent banner */}
        <CookieConsent />

        {/* Usage limit banner */}
        <UsageLimitBanner
          show={showUsageLimitBanner}
          resetTime={usageLimitResetTime}
          onClose={handleUsageLimitBannerClose}
        />

        {/* Tutorial overlay */}
        <DesktopTutorial />
      </div>
    </DesktopContext.Provider>
  )
}
